import { ChevronRight, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { C } from "@/components/modal/home";

const TELEGRAM_COMMUNITY_URL = "https://t.me/TradingSkill_Community";

const CHANNEL_KEYS = ["x", "telegram", "medium", "email"] as const;

type ChannelKey = (typeof CHANNEL_KEYS)[number];

function ChannelIcon({ id }: { id: ChannelKey }) {
  switch (id) {
    case "x":
      return (
        <svg
          viewBox="0 0 24 24"
          fill="currentColor"
          className="w-5 h-5"
          aria-hidden
        >
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      );
    case "telegram":
      return (
        <svg
          viewBox="0 0 24 24"
          fill="currentColor"
          className="w-5 h-5"
          aria-hidden
        >
          <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
        </svg>
      );
    case "medium":
      return (
        <svg
          viewBox="0 0 24 24"
          fill="currentColor"
          className="w-5 h-5"
          aria-hidden
        >
          <path d="M13.54 12a6.8 6.8 0 01-6.77 6.82A6.8 6.8 0 010 12a6.8 6.8 0 016.77-6.82A6.8 6.8 0 0113.54 12zM20.96 12c0 3.54-1.51 6.42-3.38 6.42-1.87 0-3.39-2.88-3.39-6.42s1.52-6.42 3.39-6.42 3.38 2.88 3.38 6.42M24 12c0 3.17-.53 5.75-1.19 5.75-.66 0-1.19-2.58-1.19-5.75s.53-5.75 1.19-5.75C23.47 6.25 24 8.83 24 12z" />
        </svg>
      );
    case "email":
      return (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="w-5 h-5"
          aria-hidden
        >
          <rect width="20" height="16" x="2" y="4" rx="2" />
          <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
        </svg>
      );
    default:
      return null;
  }
}
function linkToChannel(id: ChannelKey) {
  switch (id) {
    case "x":
      return "https://x.com/tradingskillai";
    case "telegram":
      return "https://t.me/TradingSkill_Official";
    case "medium":
      return "https://medium.com/@forttrade.ai";
    case "email":
      return "mailto:service@tradingskill.io";
    default:
      return "#";
  }
}
export function HomeOfficialChannels() {
  const { t } = useTranslation();
  const year = new Date().getFullYear();

  return (
    <div className="mt-10 mb-6">
      <div className="text-center mb-5">
        <div
          className="text-xs font-medium tracking-widest uppercase mb-1.5"
          style={{ color: C.dim }}
        >
          {t("homePage.officialChannels.heading")}
        </div>
        <div
          className="text-sm font-semibold"
          style={{ color: "rgba(255,255,255,0.6)" }}
        >
          {t("homePage.officialChannels.subheading")}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3 mb-5">
        {CHANNEL_KEYS.map(id => (
          <button
            key={id}
            type="button"
            onClick={() => {
              const url = linkToChannel(id);
              if (url.startsWith("mailto:")) {
                window.location.href = url;
              } else {
                window.open(url, "_blank", "noopener,noreferrer");
              }
            }}
            className="flex flex-col items-center gap-2 py-3.5 rounded-xl transition-all active:scale-95"
            style={{
              background: "rgba(12,16,32,0.5)",
              border: "1px solid rgba(100,140,200,0.06)",
            }}
          >
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center"
              style={{
                background: "rgba(34,197,94,0.08)",
                color: C.primary,
              }}
            >
              <ChannelIcon id={id} />
            </div>
            <div className="text-center">
              <div
                className="text-xs font-semibold"
                style={{ color: "rgba(255,255,255,0.7)" }}
              >
                {t(`homePage.officialChannels.items.${id}.title`)}
              </div>
              <div className="text-[10px]" style={{ color: C.dim }}>
                {t(`homePage.officialChannels.items.${id}.desc`)}
              </div>
            </div>
          </button>
        ))}
      </div>

      <a
        href={TELEGRAM_COMMUNITY_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all active:scale-[0.98] no-underline"
        style={{
          background:
            "linear-gradient(135deg, rgba(34,197,94,0.06), rgba(59,130,246,0.04))",
          border: "1px solid rgba(34,197,94,0.1)",
        }}
      >
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
          style={{ background: "rgba(34,197,94,0.12)" }}
        >
          <Users size={16} style={{ color: C.primary }} />
        </div>
        <div className="flex-1 text-left min-w-0">
          <div
            className="text-xs font-semibold"
            style={{ color: "rgba(255,255,255,0.7)" }}
          >
            {t("homePage.officialChannels.joinCommunity")}
          </div>
          <div className="text-[10px]" style={{ color: C.dim }}>
            {t("homePage.officialChannels.joinCommunityDesc")}
          </div>
        </div>
        <ChevronRight size={16} style={{ color: C.dim }} className="shrink-0" />
      </a>

      <div className="text-center mt-5 pb-2">
        <div className="text-[10px]" style={{ color: "rgba(100,140,200,0.2)" }}>
          {t("homePage.officialChannels.copyright", { year })}
        </div>
      </div>
    </div>
  );
}
